import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Pin,
  ArrowRight,
  Rocket,
  BarChart3,
  Calendar,
  MessageSquare,
  Wrench,
  Megaphone,
  ChefHat,
  Box,
  Waves,
  BookOpen,
  type LucideIcon,
} from 'lucide-react';
import { Button, Badge, EmptyState } from '.';
import { AppPreview } from './AppPreview';
import { z } from 'zod';
import { readStoredJson } from '../read-stored-json';
import type { MiniAppManifest } from '../types';
import { MRU_KEY } from '../constants';
import { FLOW_APP_SLUGS } from '../registry';

// ── Icon gradient mapping ────────────────────────────────────────────────────

const ICON_CONFIG: Record<string, { from: string; to: string; Icon: LucideIcon }> = {
  rocket:   { from: 'from-tangerine-500', to: 'to-pomegranate-500', Icon: Rocket },
  chart:    { from: 'from-matcha-500',    to: 'to-matcha-700',      Icon: BarChart3 },
  calendar: { from: 'from-blueberry-500', to: 'to-ube-500',         Icon: Calendar },
  message:  { from: 'from-dragonfruit-500', to: 'to-ube-500',       Icon: MessageSquare },
  wrench:   { from: 'from-lemon-500',     to: 'to-tangerine-500',   Icon: Wrench },
  megaphone: { from: 'from-blueberry-500', to: 'to-blueberry-700',  Icon: Megaphone },
  'chef-hat': { from: 'from-pomegranate-500', to: 'to-tangerine-500', Icon: ChefHat },
  'book-open': { from: 'from-ube-500',        to: 'to-blueberry-500',   Icon: BookOpen },
};
const DEFAULT_ICON = { from: 'from-nightshade-500', to: 'to-nightshade-700', Icon: Box };

function getIconConfig(icon?: string) {
  if (icon && ICON_CONFIG[icon]) return ICON_CONFIG[icon];
  if (icon && process.env.NODE_ENV !== 'production') {
    console.warn(`[AppDirectory] No icon config for "${icon}" — using default. Add it to ICON_CONFIG.`);
  }
  return DEFAULT_ICON;
}

// ── localStorage helpers ─────────────────────────────────────────────────────

const PINNED_KEY = 'terra-pinned-apps';

const StringListSchema = z.array(z.string());

function loadPinned(): string[] {
  return readStoredJson(localStorage, PINNED_KEY, StringListSchema, []);
}

function savePinned(slugs: string[]) {
  localStorage.setItem(PINNED_KEY, JSON.stringify(slugs));
}

function loadMru(): string[] {
  return readStoredJson(localStorage, MRU_KEY, StringListSchema, []);
}

function getInitialSlug(apps: Array<{ manifest: MiniAppManifest }>): string {
  const allSlugs = apps.map(a => a.manifest.slug);
  const mru = loadMru();
  const mruFirst = mru.find(s => allSlugs.includes(s));
  if (mruFirst) return mruFirst;
  const pinned = loadPinned();
  const pinnedFirst = pinned.find(s => allSlugs.includes(s));
  if (pinnedFirst) return pinnedFirst;
  return apps[0]?.manifest.slug ?? '';
}

// ── Types ────────────────────────────────────────────────────────────────────

interface AppDirectoryProps {
  apps: Array<{ manifest: MiniAppManifest }>;
}

interface AppGroup {
  key: string;
  title: string;
  pinIcon?: boolean;
  apps: MiniAppManifest[];
}

// ── Component ────────────────────────────────────────────────────────────────

export function AppDirectory({ apps }: AppDirectoryProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [pinnedSlugs, setPinnedSlugs] = useState<string[]>(loadPinned());
  const [selectedSlug, setSelectedSlug] = useState<string>(getInitialSlug(apps));

  const togglePin = useCallback((slug: string) => {
    const current = loadPinned();
    const next = current.includes(slug)
      ? current.filter((s: string) => s !== slug)
      : [...current, slug];
    savePinned(next);
    setPinnedSlugs(next);
  }, []);

  // ── Search filtering ──────────────────────────────────────────────────

  const filteredApps = useMemo(() => {
    if (!search.trim()) return apps;
    const q = search.toLowerCase();
    return apps.filter(({ manifest: m }) =>
      m.name.toLowerCase().includes(q)
      || m.description.toLowerCase().includes(q)
      || (m.team?.toLowerCase().includes(q) ?? false)
      || (m.owner?.toLowerCase().includes(q) ?? false),
    );
  }, [apps, search]);

  // ── Group building ────────────────────────────────────────────────────

  const groups = useMemo(() => {
    const manifests = filteredApps.map(a => a.manifest);
    const slugSet = new Set(manifests.map(m => m.slug));
    const mru = loadMru();
    const result: AppGroup[] = [];

    // Pinned group
    const pinnedApps = pinnedSlugs
      .filter(s => slugSet.has(s))
      .map(s => manifests.find(m => m.slug === s)!)
      .filter(Boolean);
    if (pinnedApps.length > 0) {
      result.push({ key: 'pinned', title: 'Pinned', pinIcon: true, apps: pinnedApps });
    }

    // Recent group (excluding pinned)
    const pinnedSet = new Set(pinnedSlugs);
    const recentApps = mru
      .filter(s => slugSet.has(s) && !pinnedSet.has(s))
      .map(s => manifests.find(m => m.slug === s)!)
      .filter(Boolean);
    if (recentApps.length > 0) {
      result.push({ key: 'recent', title: 'Recent', apps: recentApps });
    }

    // Team groups: ALL apps grouped by team (including pinned/recent)
    const teamMap = new Map<string, MiniAppManifest[]>();
    for (const m of manifests) {
      const team = m.team || 'Other';
      const existing = teamMap.get(team) ?? [];
      existing.push(m);
      teamMap.set(team, existing);
    }
    const sortedTeams = Array.from(teamMap.keys()).sort((a, b) => {
      if (a === 'Other') return 1;
      if (b === 'Other') return -1;
      return a.localeCompare(b);
    });
    for (const team of sortedTeams) {
      // The Flow section is ordered by FLOW_APP_SLUGS, not alphabetically
      // (today just Command Center; Playhouse and Action View were retired).
      const teamApps =
        team === 'Flow'
          ? [...teamMap.get(team)!].sort(
              (a, b) =>
                FLOW_APP_SLUGS.indexOf(a.slug as (typeof FLOW_APP_SLUGS)[number]) -
                FLOW_APP_SLUGS.indexOf(b.slug as (typeof FLOW_APP_SLUGS)[number]),
            )
          : teamMap.get(team)!;
      result.push({ key: `team:${team}`, title: team, apps: teamApps });
    }

    return result;
  }, [filteredApps, pinnedSlugs]);

  // ── Selected app ──────────────────────────────────────────────────────

  const selected = useMemo(
    () => apps.find(a => a.manifest.slug === selectedSlug)?.manifest ?? apps[0]?.manifest,
    [apps, selectedSlug],
  );

  if (!selected) {
    return (
      <div className="flex h-full items-center justify-center">
        <EmptyState icon={<Box className="w-6 h-6 text-content-placeholder" />} title="No apps available" description="Check back soon." />
      </div>
    );
  }

  const selectedIcon = getIconConfig(selected.icon);
  const SelectedIcon = selectedIcon.Icon;
  const isPinnedSelected = pinnedSlugs.includes(selected.slug);

  return (
    <div className="flex h-full">
      {/* Left panel */}
      <div className="w-60 flex-shrink-0 border-r border-border-primary flex flex-col bg-bg-primary">
        {/* Header */}
        <div className="px-4 pt-4 pb-3 flex items-center justify-between">
          <h2 className="text-[15px] font-semibold text-content-primary">Apps</h2>
          <span className="text-[11px] text-content-placeholder">{filteredApps.length}</span>
        </div>

        {/* Search */}
        <div className="px-3 pb-2.5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-content-placeholder" />
            <input
              type="text"
              placeholder="Search apps..."
              className="w-full pl-9 pr-4 py-1.5 text-xs bg-bg-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-placeholder focus:outline-none focus:ring-2 focus:ring-blueberry-200/50 focus:border-blueberry-500 transition"
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Scrollable grouped list */}
        <div className="flex-1 overflow-y-auto">
          {groups.map(group => {
            // The Flow section is a navigation group, not a preview group:
            // the header opens the /flow shell, each nested app opens its
            // tab directly (selected page shown), skipping the preview pane.
            const isFlowGroup = group.key === 'team:Flow';
            return (
            <div key={group.key}>
              {isFlowGroup ? (
                <button
                  type="button"
                  onClick={() => navigate('/flow')}
                  className="w-full flex items-center gap-1.5 px-4 pt-2.5 pb-1.5 text-[10px] font-semibold uppercase tracking-wide text-content-tertiary hover:text-blueberry-600 transition-colors"
                >
                  <Waves className="w-[11px] h-[11px]" />
                  Flow
                  <ArrowRight className="w-[10px] h-[10px] opacity-60" />
                </button>
              ) : (
              <div className="px-4 pt-2.5 pb-1.5 text-[10px] font-semibold uppercase tracking-wide text-content-tertiary">
                {group.pinIcon && (
                  <Pin className="inline w-[10px] h-[10px] align-middle mr-1 text-lemon-600" />
                )}
                {group.title}
              </div>
              )}
              <div className={isFlowGroup ? 'px-1.5 pl-4' : 'px-1.5'}>
              {group.apps.map(manifest => {
                const iconCfg = getIconConfig(manifest.icon);
                const IconComponent = iconCfg.Icon;
                const isSelected = manifest.slug === selectedSlug;
                const isPinned = pinnedSlugs.includes(manifest.slug);

                return (
                  <button
                    key={`${group.key}-${manifest.slug}`}
                    onClick={() =>
                      isFlowGroup ? navigate(`/flow/${manifest.slug}`) : setSelectedSlug(manifest.slug)
                    }
                    className={`group flex items-center gap-2.5 w-full px-3 py-2 rounded-lg transition-all duration-100 ${
                      isSelected ? 'bg-blueberry-50' : 'hover:bg-bg-secondary'
                    }`}
                  >
                    <div
                      className={`w-[30px] h-[30px] rounded-lg bg-gradient-to-br ${iconCfg.from} ${iconCfg.to} flex items-center justify-center flex-shrink-0`}
                    >
                      <IconComponent className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-[13px] truncate ${
                          isSelected
                            ? 'font-semibold text-blueberry-700'
                            : 'font-medium text-content-primary'
                        }`}
                      >
                        {manifest.name}
                      </div>
                      <div className="text-[11px] text-content-tertiary">
                        {manifest.owner ?? 'Unknown'} · {manifest.team ?? 'Other'}
                      </div>
                    </div>
                    <button
                      type="button"
                      aria-label={isPinned ? 'Unpin app' : 'Pin app'}
                      className={`flex-shrink-0 transition-opacity focus:outline-none focus:ring-2 focus:ring-blueberry-200 rounded ${
                        isPinned
                          ? 'opacity-100'
                          : 'opacity-0 group-hover:opacity-100'
                      }`}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        togglePin(manifest.slug);
                      }}
                    >
                      <Pin
                        className={`w-3.5 h-3.5 ${
                          isPinned
                            ? 'text-lemon-600 fill-lemon-600'
                            : 'text-content-placeholder'
                        }`}
                      />
                    </button>
                  </button>
                );
              })}
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 overflow-y-auto flex flex-col bg-bg-primary">
        {/* Hero */}
        <div className="px-6 pt-6 flex items-start gap-4">
          <div
            className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${selectedIcon.from} ${selectedIcon.to} flex items-center justify-center shadow-md flex-shrink-0`}
          >
            <SelectedIcon className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-content-primary">{selected.name}</h3>
            <div className="text-sm text-content-tertiary mt-0.5 flex items-center gap-2">
              <span>By {selected.owner ?? 'Unknown'}</span>
              <span className="w-[3px] h-[3px] rounded-full bg-nightshade-400" />
              <span>{selected.team ?? 'Other'}</span>
              {selected.userCount != null && (
                <>
                  <span className="w-[3px] h-[3px] rounded-full bg-nightshade-400" />
                  <span>Used by {selected.userCount} people</span>
                </>
              )}
            </div>
            {selected.team && (
              <Badge tone="indigo" className="mt-2">
                {selected.team} Team
              </Badge>
            )}
          </div>
        </div>

        {/* Description */}
        <p className="px-6 pt-4 text-sm text-content-secondary leading-relaxed">
          {selected.description}
        </p>

        {/* Preview */}
        <div className="px-6 pt-4 pb-4">
          <AppPreview previewType={selected.previewType} appName={selected.name} />
        </div>

        {/* Actions */}
        <div className="px-6 py-4 flex gap-2.5">
          <Button variant="primary" onClick={() => navigate(`/tools/${selected.slug}`)}>
            <ArrowRight className="w-3.5 h-3.5" />
            Open App
          </Button>
          <Button variant="neutral" onClick={() => togglePin(selected.slug)}>
            <Pin className="w-3.5 h-3.5" />
            {isPinnedSelected ? 'Unpin' : 'Pin'}
          </Button>
        </div>
      </div>
    </div>
  );
}
